import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';

// Define group to accessible paths mapping
const groupPathAccess = {
  engage_administrators: [
    '/forms',
    '/home',
    '/login',
    '/reports',
    '/contact-center',
    '/rss/links',
    '/admin',
    '/admin/workforce/skilling-sheet',
    '/admin/users',
    '/admin/users/invites',
    '/admin/users/:userId',
    '/admin/groups',
    '/files',
    '/repair/search',
    '/repair/rrf',
    '/contact-center/aurora',
  ],
  engage_us_cc_wf: [
    '/forms',
    '/home',
    '/login',
    '/reports',
    '/contact-center',
    '/rss/links',
    '/files',
    '/admin',
    '/admin/workforce/skilling-sheet',
  ],
  engage_us_cc: [
    '/forms',
    '/home',
    '/login',
    '/contact-center',
    '/rss/links',
    '/files',
    '/contact-center/aurora'
  ],
  engage_us_cc_mgmt: [
    '/forms',
    '/home',
    '/login',
    '/reports',
    '/contact-center',
    '/rss/links',
    '/files',
    '/contact-center/aurora',
  ],
  engage_us_cc_va: ['/forms', '/home', '/login', '/contact-center'],
  engage_us_fsl: ['/home', '/login', '/contact-center'],
  engage_us_cc_tn: ['/forms', '/home', '/login', '/files', '/contact-center'],
  engage_us_fst: ['/forms', '/home', '/login', '/files', '/repair/search'],
  engage_us_report: ['/home', '/login', '/reports', '/files'],
  engage_us_inv: ['/home', '/login', '/reports', '/forms', '/files'],
  engage_us_rlt: ['/home', '/login', '/forms', '/files', '/repair/search'],
  engage_us_rrf: [
    '/home',
    '/login',
    '/forms',
    '/files',
    '/reports',
    '/repair/search',
    '/repair/rrf',
  ],
};

// Create authentication context
const AuthContext = createContext();

// Custom hook to use authentication context
export const useAuth = () => useContext(AuthContext);

/** Case-insensitive group membership check against auth userGroups. */
export const userHasGroup = (userGroups, groupName) => {
  if (!Array.isArray(userGroups) || !groupName) {
    return false;
  }
  const normalizedGroup = String(groupName).toLowerCase();
  return userGroups.some(
    group => String(group).toLowerCase() === normalizedGroup,
  );
};

/** Map the current URL to the auth path required for access checks. */
export const getRequiredAccessPath = (pathname, fallbackPath = '') => {
  if (!pathname) {
    return fallbackPath;
  }
  if (
    pathname === '/contact-center' ||
    pathname.startsWith('/contact-center/')
  ) {
    return '/contact-center';
  }
  return fallbackPath || pathname;
};

/** Route-level access check; Aurora uses in-tab messaging for feature permission. */
export const canAccessPath = (pathname, accessiblePaths) => {
  if (!Array.isArray(accessiblePaths)) {
    return false;
  }

  if (
    pathname === '/contact-center' ||
    pathname.startsWith('/contact-center/')
  ) {
    if (accessiblePaths.includes('/contact-center')) {
      return true;
    }

    const isAuroraPath =
      pathname === '/contact-center/aurora' ||
      pathname.startsWith('/contact-center/aurora/');

    return isAuroraPath && accessiblePaths.includes('/contact-center/aurora');
  }

  const requiredAccessPath = getRequiredAccessPath(pathname, pathname);
  return accessiblePaths.includes(requiredAccessPath);
};

export const getDefaultContactCenterPath = accessiblePaths => {
  if (!Array.isArray(accessiblePaths)) {
    return '/contact-center/comm-devices';
  }
  if (accessiblePaths.includes('/contact-center')) {
    return '/contact-center/comm-devices';
  }
  if (accessiblePaths.includes('/contact-center/aurora')) {
    return '/contact-center/aurora';
  }
  return '/contact-center/comm-devices';
};

const getInitialState = () => {
  const currentUser = sessionStorage.getItem('user');
  return currentUser ? JSON.parse(currentUser) : null;
};

// Authentication provider component
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = Cookies.get('token') || Cookies.get('localToken');
    return !!token;
  });
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(getInitialState);
  const [userGroups, setUserGroups] = useState([]);
  const [accessiblePaths, setAccessiblePaths] = useState([]);

  useEffect(() => {
    sessionStorage.setItem('user', JSON.stringify(user));
  }, [user]);

  // Function to get the access token from cookies (check both SSO and local tokens)
  const getToken = () => Cookies.get('token') || Cookies.get('localToken');

  // Function to log in the user and set the authentication state
  const login = (userDetails, authType = 'localAuth') => {
    // Check if the login is for SSO or Local based on the authType parameter
    if (authType === 'SSO') {
      Cookies.set('token', userDetails.token); // Set SSO token
    } else if (authType === 'localAuth') {
      Cookies.set('localToken', userDetails.token); // Set local auth token
    }

    setUser(userDetails);
    setIsAuthenticated(true);

    // Normalize group names and filter
    const filteredGroups = userDetails.groups.filter(group =>
      group.toLowerCase().startsWith('engage_'),
    );

    const normalizedGroupPathAccess = Object.keys(groupPathAccess).reduce(
      (acc, key) => {
        acc[key.toLowerCase()] = groupPathAccess[key];
        return acc;
      },
      {},
    );

    // Get accessible paths for the user based on their groups
    const userPaths = filteredGroups
      .flatMap(group => normalizedGroupPathAccess[group.toLowerCase()] || [])
      .filter((value, index, self) => self.indexOf(value) === index);

    setUserGroups(filteredGroups);
    setAccessiblePaths(userPaths);

    // Persist user groups in session storage
    sessionStorage.setItem('userGroups', JSON.stringify(filteredGroups));
  };

  // Function to log out the user and clear the authentication state and cookies
  const logout = () => {
    Cookies.remove('token');
    Cookies.remove('localToken');
    setUser(null);
    setIsAuthenticated(false);
    setUserGroups([]);
    setAccessiblePaths([]);
  };

  // Check if the user is authenticated on component mount
  useEffect(() => {
    const storedGroups = JSON.parse(
      sessionStorage.getItem('userGroups') || '[]',
    );

    if (storedGroups.length > 0) {
      setUserGroups(storedGroups);
      const normalizedGroupPathAccess = Object.keys(groupPathAccess).reduce(
        (acc, key) => {
          acc[key.toLowerCase()] = groupPathAccess[key];
          return acc;
        },
        {},
      );

      // Get accessible paths for the user based on their stored groups
      const paths = storedGroups
        .flatMap(group => normalizedGroupPathAccess[group.toLowerCase()] || [])
        .filter((value, index, self) => self.indexOf(value) === index);

      setAccessiblePaths(paths);
    } else {
      setAccessiblePaths([]);
    }

    // Check if the user is authenticated based on the presence of either token
    setIsAuthenticated(!!Cookies.get('token') || !!Cookies.get('localToken'));
    setLoading(false);
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        getToken,
        login,
        logout,
        userGroups,
        accessiblePaths,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
